import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { AppButton } from '../../components/AppButton';
import { AppDateField } from '../../components/AppDateField';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import { useTimeLogEmployee } from '../../hooks/useTimeLogEmployee';
import {
  addTaskTimeLog,
  deleteTaskTimeLog,
  listTaskTimeLogs,
  updateTaskTimeLog,
} from '../../services/projectService';
import type { ProjectAssignee, ProjectMember, TaskTimeLog } from '../../types/project';
import {
  calendarDateFromApi,
  formatTime,
  todayBusinessCalendarDate,
} from '../../utils/dateTime';
import { formatDurationCompact } from '../../utils/projectTaskForm';
import { projectErrorMessage } from '../../utils/projectErrors';
import {
  clockFromApi,
  employeeInitials,
  fallbackLoggedTime,
  groupTimeLogs,
  logsForDisplay,
  timeLogSourceLabel,
  timeRangeError,
} from '../../utils/taskTimeLog';
import { runOperation } from '../../utils/operationFeedback';
import { TimeEntryFields } from './TimeEntryFields';

type Props = {
  projectId: number;
  taskId: number;
  taskName: string;
  canEdit: boolean;
  existingMinutes?: number;
  employeeId?: number | null;
  employeeName?: string | null;
  members: ProjectMember[];
  assignees: ProjectAssignee[];
  onBack: () => void;
  onChanged: (timeSpentMinutes: number) => void;
  /**
   * Clears time spent that was recorded without any entries (shown as one
   * "Recorded time" row). Resolves true once the task is saved.
   */
  onClearRecordedTime?: () => Promise<boolean>;
};

/** Which entry the form is editing; 0 is the recorded-time row. */
type FormMode = { kind: 'add' } | { kind: 'edit'; log: TaskTimeLog };

export function TaskTimeLogView({
  projectId,
  taskId,
  taskName,
  canEdit,
  existingMinutes = 0,
  employeeId,
  employeeName,
  members,
  assignees,
  onBack,
  onChanged,
  onClearRecordedTime,
}: Props) {
  const palette = useAppColors();
  const employee = useTimeLogEmployee({ assignees, members });
  const [logs, setLogs] = useState<TaskTimeLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<FormMode | null>(null);
  const [date, setDate] = useState(todayBusinessCalendarDate);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [formError, setFormError] = useState('');
  const [pendingDelete, setPendingDelete] = useState<TaskTimeLog | null>(null);

  const themed = useMemo(
    () => ({
      title: { ...typography.screenTitle, color: palette.textPrimary, fontSize: 24 },
      taskName: { ...typography.caption, color: palette.textSecondary, fontWeight: '600' as const },
      add: { ...typography.body, color: palette.primary, fontWeight: '700' as const },
      day: { ...typography.body, color: palette.textPrimary, fontWeight: '700' as const },
      meta: { ...typography.caption, color: palette.textSecondary },
      name: { ...typography.body, color: palette.textPrimary, fontWeight: '600' as const },
      action: { ...typography.caption, color: palette.primary, fontWeight: '700' as const },
      danger: { ...typography.caption, color: palette.error, fontWeight: '700' as const },
      error: { ...typography.caption, color: palette.error },
      empty: { ...typography.body, color: palette.textMuted },
    }),
    [palette],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listTaskTimeLogs(projectId, taskId);
      setLogs(result.logs);
      if (result.logs.length || result.timeSpentMinutes > 0) {
        onChanged(result.timeSpentMinutes || existingMinutes);
      }
      setError('');
    } catch (caught) {
      setError(projectErrorMessage(caught, 'Unable to load the time log.'));
    } finally {
      setLoading(false);
    }
  }, [existingMinutes, projectId, taskId]);

  useEffect(() => {
    void load();
  }, [load]);

  const groups = groupTimeLogs(
    logsForDisplay(
      logs,
      fallbackLoggedTime({
        taskId,
        minutes: existingMinutes,
        employeeId,
        employeeName,
      }),
    ),
  );

  function openForm(next: FormMode) {
    setForm(next);
    setFormError('');
    if (next.kind === 'edit' && next.log.timeLogId > 0) {
      setDate(calendarDateFromApi(next.log.startedAt) ?? todayBusinessCalendarDate());
      setStartTime(clockFromApi(next.log.startedAt));
      setEndTime(clockFromApi(next.log.endedAt));
      return;
    }
    // New entries, and the recorded-time row (which has no real range).
    setDate(todayBusinessCalendarDate());
    setStartTime('');
    setEndTime('');
  }

  function closeForm() {
    setForm(null);
    setFormError('');
    setStartTime('');
    setEndTime('');
  }

  function canChange(log: TaskTimeLog) {
    if (!canEdit) return false;
    if (log.timeLogId <= 0) return true;
    return employee.canLogForOthers || log.employeeId === employee.selfEmployeeId;
  }

  async function saveEntry() {
    if (!canEdit || busy || !form) return;
    const rangeError = timeRangeError(startTime, endTime);
    if (rangeError) {
      setFormError(rangeError);
      return;
    }
    const editing = form.kind === 'edit' && form.log.timeLogId > 0 ? form.log : null;
    if (!editing && employee.missingMessage) {
      setFormError(employee.missingMessage);
      return;
    }
    setBusy(true);
    setFormError('');
    try {
      if (editing) {
        const result = await runOperation('update', () =>
          updateTaskTimeLog(projectId, taskId, editing.timeLogId, {
            date,
            startTime,
            endTime,
          }),
        );
        setLogs(current =>
          current
            .filter(item => item.timeLogId !== result.replacedTimeLogId)
            .concat(result.log ? [result.log] : []),
        );
        onChanged(result.timeSpentMinutes);
      } else {
        // Adding the first entry replaces any recorded-only time spent.
        const result = await runOperation('add', () =>
          addTaskTimeLog(projectId, taskId, {
            date,
            startTime,
            endTime,
            source: 'MANUAL',
            ...(employee.requestEmployeeId != null
              ? { employeeId: employee.requestEmployeeId }
              : {}),
          }),
        );
        setLogs(current => (result.log ? [result.log, ...current] : current));
        onChanged(result.timeSpentMinutes);
      }
      closeForm();
    } catch (caught) {
      setFormError(
        projectErrorMessage(
          caught,
          editing ? 'Unable to update the time entry.' : 'Unable to add the time log.',
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  async function deleteEntry(log: TaskTimeLog) {
    setBusy(true);
    try {
      if (log.timeLogId <= 0) {
        // Recorded without entries: clearing it saves the task with 0 spent.
        if (await onClearRecordedTime?.()) onChanged(0);
        return;
      }
      const result = await runOperation(
        'delete',
        () => deleteTaskTimeLog(projectId, taskId, log.timeLogId),
        {
          failureDetail: caught =>
            projectErrorMessage(caught, 'The time log could not be deleted.'),
        },
      );
      setLogs(current => current.filter(item => item.timeLogId !== log.timeLogId));
      onChanged(result.timeSpentMinutes);
      if (form?.kind === 'edit' && form.log.timeLogId === log.timeLogId) closeForm();
    } catch {
      // runOperation already showed the "Delete failed" popup.
    } finally {
      setBusy(false);
      setPendingDelete(null);
    }
  }

  const editingRecorded = form?.kind === 'edit' && form.log.timeLogId <= 0;

  return (
    <View style={styles.body}>
      <View style={styles.header}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Back"
          onPress={onBack}
          hitSlop={8}
        >
          <Text style={themed.taskName}>← {taskName}</Text>
        </Pressable>
        {canEdit ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Add entry"
            onPress={() => (form?.kind === 'add' ? closeForm() : openForm({ kind: 'add' }))}
            hitSlop={8}
          >
            <Text style={themed.add}>+ ADD ENTRY</Text>
          </Pressable>
        ) : null}
      </View>
      <Text style={themed.title}>Time log</Text>

      {form ? (
        <View style={styles.form}>
          <Text style={themed.day}>
            {form.kind === 'add' ? 'New time entry' : 'Edit time entry'}
          </Text>
          {editingRecorded ? (
            <Text style={themed.meta}>
              This time was recorded without From / To times. Saving a range
              replaces it with a time entry.
            </Text>
          ) : null}
          <AppDateField label="Date" value={date} onChange={setDate} />
          <TimeEntryFields
            startTime={startTime}
            endTime={endTime}
            onChangeStart={value => {
              setStartTime(value);
              setFormError('');
            }}
            onChangeEnd={value => {
              setEndTime(value);
              setFormError('');
            }}
            error={formError}
            required
            employee={form.kind === 'edit' && !editingRecorded ? undefined : employee}
          />
          <View style={styles.formActions}>
            <AppButton title="Cancel" variant="secondary" onPress={closeForm} />
            <AppButton
              title={form.kind === 'add' ? 'Save entry' : 'Save changes'}
              onPress={() => void saveEntry()}
              loading={busy}
            />
          </View>
        </View>
      ) : null}

      {loading ? <Text style={themed.empty}>Loading time log…</Text> : null}
      {error ? <Text style={themed.error}>{error}</Text> : null}
      {!loading && !groups.length && !error ? (
        <Text style={themed.empty}>No time has been logged yet.</Text>
      ) : null}

      {groups.map(group => (
        <View key={group.date} style={styles.day}>
          <View style={styles.dayHeader}>
            <Text style={themed.day}>{group.label}</Text>
            <Text style={themed.day}>{group.totalLabel}</Text>
          </View>
          {group.entries.map(log => {
            const badge = timeLogSourceLabel(log.source);
            const recorded = log.timeLogId <= 0;
            const rangeLabel = recorded
              ? 'Recorded without From / To'
              : `From ${formatTime(log.startedAt)}  To ${formatTime(log.endedAt)}`;
            return (
              <View key={log.timeLogId} testID={`time-log-row-${log.timeLogId}`} style={styles.row}>
                <View style={[styles.avatar, { backgroundColor: palette.primary }]}>
                  <Text style={[styles.avatarText, { color: palette.onPrimary }]}>
                    {employeeInitials(log.employeeName)}
                  </Text>
                </View>
                <View style={styles.flex}>
                  <Text style={themed.name}>{log.employeeName}</Text>
                  <Text style={themed.meta}>{rangeLabel}</Text>
                </View>
                <Text style={themed.name}>
                  {formatDurationCompact(log.durationMinutes) || '0m'}
                </Text>
                <View
                  style={[
                    styles.badge,
                    { backgroundColor: badge !== 'M' ? palette.success : palette.border },
                  ]}
                >
                  <Text
                    style={[
                      styles.badgeText,
                      { color: badge !== 'M' ? palette.onSuccess : palette.textSecondary },
                    ]}
                  >
                    {badge}
                  </Text>
                </View>
                {canChange(log) && (!recorded || onClearRecordedTime) ? (
                  <View style={styles.rowActions}>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={`Edit ${log.employeeName} time entry`}
                      onPress={() => openForm({ kind: 'edit', log })}
                      disabled={busy}
                      hitSlop={8}
                    >
                      <Text style={themed.action}>Edit</Text>
                    </Pressable>
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel={`Delete ${log.employeeName} time entry`}
                      onPress={() => setPendingDelete(log)}
                      disabled={busy}
                      hitSlop={8}
                    >
                      <Text style={themed.danger}>Delete</Text>
                    </Pressable>
                  </View>
                ) : null}
              </View>
            );
          })}
        </View>
      ))}
      <ConfirmDialog
        visible={pendingDelete != null}
        title="Confirm to delete?"
        destructive
        busy={busy}
        onConfirm={() => {
          if (pendingDelete) void deleteEntry(pendingDelete);
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  body: { gap: spacing.md },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  form: { gap: spacing.sm },
  formActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm },
  flex: { flex: 1, minWidth: 0 },
  day: { gap: spacing.sm, marginTop: spacing.sm },
  dayHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: spacing.sm,
    minHeight: 44,
  },
  rowActions: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: { ...typography.caption, fontWeight: '700' },
  badge: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeText: { fontSize: 11, fontWeight: '700' },
  empty: { marginTop: spacing.md },
});
